import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { IconButton } from "@/components/common/IconButton";
import { SearchInput } from "@/components/common/SearchInput";
import { isImeConsumedKeyDown } from "@/lib/ime-composition";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";

/** 検索欄の幅 (px)。件数と前後移動を 1 行に収めつつ、履歴を覆いすぎない値。配置は ChatHeader が決める。 */
const BAR_WIDTH = 320;

interface ChatTranscriptSearchBarProps {
	/** 検索欄を閉じ、コンポーザーへフォーカスを戻す。 */
	onClose: () => void;
}

export function ChatTranscriptSearchBar({ onClose }: ChatTranscriptSearchBarProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const query = useTranscriptSearchStore((s) => s.query);
	const matchCount = useTranscriptSearchStore((s) => s.matchCount);
	const activeIndex = useTranscriptSearchStore((s) => s.activeIndex);
	const focusRequestId = useTranscriptSearchStore((s) => s.focusRequestId);
	const openSearch = useTranscriptSearchStore((s) => s.openSearch);
	const setQuery = useTranscriptSearchStore((s) => s.setQuery);
	const stepMatch = useTranscriptSearchStore((s) => s.stepMatch);

	// 検索欄を開く操作のたびに (既に開いていても) 入力欄へフォーカスを戻す。
	useEffect(() => {
		inputRef.current?.focus();
		inputRef.current?.select();
	}, [focusRequestId]);

	const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
		// Safari は変換確定の Enter が isComposing=false で届くため、keyCode も見る共通判定を使う。
		if (isImeConsumedKeyDown(event.nativeEvent)) return;
		// この欄にフォーカスがある間の Ctrl/Cmd+F は、開く操作と同じ扱いにして検索語を選び直させる
		// (素通しするとブラウザ標準の検索バーがこの欄の上に開いてしまう)。
		if (event.key === "f" && (event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey) {
			event.preventDefault();
			openSearch();
			return;
		}
		if (event.key === "Enter") {
			event.preventDefault();
			stepMatch(event.shiftKey ? -1 : 1);
		}
		if (event.key === "Escape") {
			event.preventDefault();
			onClose();
		}
	};

	const hasMatch = matchCount > 0;
	const countLabel = !hasMatch
		? query.length > 0
			? "一致なし"
			: ""
		: `${activeIndex + 1}/${matchCount}`;

	return (
		<div
			data-testid="transcript-search-bar"
			style={{
				width: BAR_WIDTH,
				display: "flex",
				alignItems: "center",
				gap: 4,
				padding: "4px 6px",
				background: "var(--surface-2)",
				borderRadius: "var(--radius-2)",
				boxShadow: "var(--shadow-popover)",
			}}
		>
			<SearchInput
				ref={inputRef}
				variant="framed"
				value={query}
				onChange={setQuery}
				onKeyDown={handleKeyDown}
				placeholder="チャット履歴を検索"
				testId="transcript-search-input"
			/>
			<span
				data-testid="transcript-search-count"
				style={{
					flexShrink: 0,
					minWidth: 48,
					textAlign: "right",
					fontFamily: "var(--font-mono)",
					fontSize: "var(--text-caption)",
					color: "var(--fg-3)",
				}}
			>
				{countLabel}
			</span>
			<IconButton
				icon="chevronU"
				iconSize={14}
				size={22}
				disabled={!hasMatch}
				title="前の一致"
				onClick={() => stepMatch(-1)}
				data-testid="transcript-search-previous"
			/>
			<IconButton
				icon="chevronD"
				iconSize={14}
				size={22}
				disabled={!hasMatch}
				title="次の一致"
				onClick={() => stepMatch(1)}
				data-testid="transcript-search-next"
			/>
			<IconButton
				icon="x"
				iconSize={14}
				size={22}
				onClick={onClose}
				data-testid="transcript-search-close"
			/>
		</div>
	);
}
